import { ReincludeDialog } from "./ReincludeDialog";
import { useEffect, useRef, useState } from "react";
import { Pencil, Plus, ShieldCheck, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/v2/spinner";
import { cn } from "@/utils/cn";
import {
  useGetItemDetailQuery,
  useGetWpsExcludedQuery,
  useUpdateItemAmountsMutation,
} from "@/services/(company)/payrollRunsApi";
import type {
  IPayrollRunItemLine,
  IWpsExcludedEmployee,
} from "@/types/(company)/payrollRuns.interface";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

type BadgeVariant =
  "neutral" | "info" | "warning" | "danger" | "primary" | "success";

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

type RowIssue = {
  badges: { label: string; variant: BadgeVariant }[];
  help: string;
  blocking: boolean;
};

// Payment-method color tags (match the Active list).
const buildPaymentMethodMeta = (
  t: TFunction,
): Record<string, { label: string; variant: BadgeVariant }> => ({
  WPS: { label: "WPS", variant: "primary" },
  BANK_TRANSFER: {
    label: t("company:payrolls.bank_transfer"),
    variant: "info",
  },
  CASH: { label: t("company:payrolls.cash"), variant: "warning" },
  CHEQUE: { label: t("company:payrolls.cheque"), variant: "neutral" },
});

/**
 * ALL issues on a row (not just the first) so HR sees the full picture — e.g.
 * missing WPS ID *and* zero net pay together. `blocking` = has a fixable issue
 * that stops approval (missing bank data or negative net). Zero net (exactly 0)
 * is shown for context but doesn't block.
 */
const getRowIssues = (r: IWpsExcludedEmployee, t: TFunction): RowIssue => {
  if (r.reason === "EXCLUDED_STATUS") {
    const failed = r.itemStatus === "FAILED";
    return {
      badges: [
        failed
          ? {
              label: t("company:payrolls.generation_failed"),
              variant: "danger",
            }
          : { label: t("company:payrolls.excluded"), variant: "neutral" },
      ],
      help: t("company:payrolls.excluded_from_this_run"),
      blocking: false,
    };
  }

  // Which bank fields are missing for this method (WPS / Bank Transfer only).
  const bankMissing: string[] = [];
  if (r.paymentMethod === "WPS") {
    if (!r.hasIban) bankMissing.push("IBAN");
    if (!r.hasRouting) bankMissing.push(t("company:payrolls.routing_code"));
    if (!r.hasPersonId) bankMissing.push(t("company:payrolls.wps_id"));
  } else if (r.paymentMethod === "BANK_TRANSFER") {
    if (!r.hasIban) bankMissing.push("IBAN");
    if (!r.hasBankName) bankMissing.push(t("company:payrolls.bank_name_field"));
  }

  const netIssue = r.netPay < 0 ? "negative" : r.netPay === 0 ? "zero" : null;

  // All issues as badges (net one keeps its color; bank ones neutral to keep
  // it calm) + a plain-English help line.
  const badges: { label: string; variant: BadgeVariant }[] = [];
  const helpParts: string[] = [];

  if (netIssue === "negative") {
    badges.push({
      label: t("company:payrolls.negative_net_pay"),
      variant: "danger",
    });
    helpParts.push(t("company:payrolls.deductions_exceed_earnings_help"));
  } else if (netIssue === "zero") {
    badges.push({
      label: t("company:payrolls.zero_net_pay"),
      variant: "warning",
    });
    helpParts.push(t("company:payrolls.nothing_payable_help"));
  }

  bankMissing.forEach((f) =>
    badges.push({
      label: t("company:payrolls.missing_field", { field: f }),
      variant: "danger",
    }),
  );
  // An account number that is not a UAE IBAN (B50): the bank file cannot
  // use it. It is not "missing", so the checks above do not see it.
  if (r.reason === "INVALID_IBAN") {
    badges.push({
      label: t("company:payrolls.invalid_iban"),
      variant: "danger",
    });
    helpParts.push(t("company:payrolls.invalid_iban_help"));
  }
  if (bankMissing.length)
    helpParts.push(
      netIssue
        ? t("company:payrolls.also_add_fields_prompt", {
            fields: bankMissing.join(", "),
          })
        : t("company:payrolls.add_fields_prompt", {
            fields: bankMissing.join(", "),
          }),
    );

  if (!badges.length)
    badges.push({ label: t("company:payrolls.excluded"), variant: "neutral" });

  return {
    badges,
    help: helpParts.join(" "),
    // The server's own test, the one approval refuses on (B33); the local
    // guess only for an older API that does not send it.
    blocking:
      r.blocksApproval ??
      (r.netPay < 0 || bankMissing.length > 0 || r.reason === "INVALID_IBAN"),
  };
};

interface IProps {
  runId: number;
  /** The run is in review, so an employee HR excluded can be put back (D3). */
  canReinclude?: boolean;
}

/**
 * A WPS exclusion reason, as the run page labels it. This used to read an
 * undefined `REASON_META`, so "saved, but still missing …" became a generic
 * error instead.
 */
const wpsReasonLabel = (t: TFunction, reason: string): string =>
  ({
    EXCLUDED_STATUS: t("company:payrolls.excluded_from_run"),
    ON_HOLD: t("company:payrolls.salary_on_hold"),
    MISSING_PERSON_ID: t("company:payrolls.missing_wps_id"),
    MISSING_ROUTING: t("company:payrolls.missing_routing_code"),
    MISSING_IBAN: t("company:payrolls.missing_iban"),
    INVALID_IBAN: t("company:payrolls.invalid_iban"),
    MISSING_BANK_NAME: t("company:payrolls.missing_bank_name"),
    ZERO_NET_PAY: t("company:payrolls.zero_negative_net_pay"),
  })[reason] ?? reason;

export const WpsExcludedTab = ({ runId, canReinclude = false }: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [reincludeRow, setReincludeRow] = useState<IWpsExcludedEmployee | null>(
    null,
  );
  const { data, isLoading, refetch } = useGetWpsExcludedQuery(runId, {
    skip: !runId || isNaN(runId),
    refetchOnMountOrArgChange: true,
  });

  const [editRow, setEditRow] = useState<IWpsExcludedEmployee | null>(null);
  // Rows fading out after being resolved (before removal from the list).
  const [removingIds, setRemovingIds] = useState<Set<number>>(new Set());
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());

  const rows: IWpsExcludedEmployee[] = (data?.data?.items ?? []).filter(
    (r) => !removedIds.has(r.itemId),
  );
  // Only fixable rows block approval; legit zero-net doesn't.
  const blockingCount = rows.filter((r) => getRowIssues(r, t).blocking).length;

  const openEdit = (r: IWpsExcludedEmployee) => setEditRow(r);

  // Resolved → fade the row out, then drop it from the list.
  const markResolved = (itemId: number) => {
    setEditRow(null);
    toast.success(t("company:payrolls.resolved"));
    setRemovingIds((prev) => new Set(prev).add(itemId));
    setTimeout(() => {
      setRemovedIds((prev) => new Set(prev).add(itemId));
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      refetch();
    }, 350);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <ShieldCheck className="h-8 w-8 text-success" />
        <p className="text-sm font-semibold text-foreground">
          {t("company:payrolls.no_issues")}
        </p>
        <p className="text-xs text-muted-foreground max-w-sm">
          {t(
            "company:payrolls.every_employee_has_payable_net_pay_and_valid_payment_det",
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground max-w-2xl">
        <Trans
          i18nKey="company:payrolls.blocking_count"
          count={blockingCount}
          components={[<b className="text-danger" key="0" />]}
        />
      </p>

      <div className="overflow-x-auto scrollbar-thin rounded-xs border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-start text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2.5 font-semibold">
                {t("common:labels.employee")}
              </th>
              <th className="px-4 py-2.5 font-semibold">
                {t("company:payrolls.issue")}
              </th>
              <th className="px-4 py-2.5 font-semibold">
                {t("company:payrolls.net_pay")}
              </th>
              <th className="px-4 py-2.5 font-semibold">
                {t("company:payrolls.payment")}
              </th>
              <th className="px-4 py-2.5 font-semibold text-end">
                {t("company:payrolls.action")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const { badges, help, blocking } = getRowIssues(r, t);
              const displayName = r.name ?? `Employee #${r.userId}`;
              const canFix = blocking;
              const isRemoving = removingIds.has(r.itemId);
              return (
                <tr
                  key={r.itemId}
                  className={cn(
                    "border-b border-border last:border-0 transition-all duration-300",
                    isRemoving && "opacity-0",
                  )}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="size-9">
                        {r.profileImage && (
                          <AvatarImage src={r.profileImage} alt={displayName} />
                        )}
                        <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                          {getInitials(displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium text-foreground truncate">
                          {displayName}
                        </span>
                        {r.employeeCode && (
                          <span className="text-xs text-muted-foreground">
                            {r.employeeCode}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {badges.map((b) => (
                          <Badge key={b.label} variant={b.variant}>
                            {b.label}
                          </Badge>
                        ))}
                      </div>
                      {help && (
                        <span className="text-xs text-muted-foreground max-w-xs">
                          {help}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-foreground">
                    {r.netPay.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-4 py-3">
                    {r.paymentMethod ? (
                      <Badge
                        variant={
                          buildPaymentMethodMeta(t)[r.paymentMethod]?.variant ??
                          "neutral"
                        }
                      >
                        {buildPaymentMethodMeta(t)[r.paymentMethod]?.label ??
                          r.paymentMethod}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-end">
                    {canFix ? (
                      <PermissionButton
                        section={PLATFORM_SECTION_ENUM.PAYROLL}
                        action="edit"
                        mode="hide"
                        variant="outline-primary"
                        size="xs"
                        onClick={() => openEdit(r)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        {t("company:payrolls.edit_and_resolve")}
                      </PermissionButton>
                    ) : canReinclude && r.reason === "EXCLUDED_STATUS" ? (
                      <PermissionButton
                        section={PLATFORM_SECTION_ENUM.PAYROLL}
                        action="edit"
                        mode="hide"
                        variant="outline"
                        size="xs"
                        onClick={() => setReincludeRow(r)}
                      >
                        {t("company:payrolls.reinclude")}
                      </PermissionButton>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {t("company:payrolls.wont_block")}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ReincludeDialog
        runId={runId}
        itemId={reincludeRow?.itemId ?? null}
        name={reincludeRow?.name ?? null}
        onClose={() => setReincludeRow(null)}
      />

      {editRow && (
        <ResolveDialog
          runId={runId}
          row={editRow}
          onClose={() => setEditRow(null)}
          onResolved={markResolved}
        />
      )}
    </div>
  );
};

// UAE WPS identity fields are numeric; IBAN keeps its AE prefix (alphanumeric).
const sanitizeBankField = (
  key: "bankName" | "accountNumber" | "routingNumber" | "wpsPersonId",
  value: string,
): string => {
  if (key === "routingNumber" || key === "wpsPersonId")
    return value.replace(/\D/g, "");
  if (key === "accountNumber")
    return value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  return value;
};

const buildFields = (
  t: TFunction,
): {
  key: "bankName" | "accountNumber" | "routingNumber" | "wpsPersonId";
  label: string;
  placeholder: string;
}[] => [
  {
    key: "bankName",
    label: t("company:payrolls.bank_name"),
    placeholder: "e.g. Emirates NBD",
  },
  {
    key: "accountNumber",
    label: t("company:payrolls.account_number_iban"),
    placeholder: "AE07 0331 2345 6789 0123 456",
  },
  {
    key: "routingNumber",
    label: t("company:payrolls.routing_code"),
    placeholder: t("company:payrolls.bank_routing_agent_code"),
  },
  {
    key: "wpsPersonId",
    label: t("company:payrolls.wps_person_id"),
    placeholder: t("company:payrolls.labour_card_mol_person_id"),
  },
];

interface IAmountsDialogProps {
  runId: number;
  row: IWpsExcludedEmployee;
  onClose: () => void;
  onResolved: (itemId: number) => void;
}

const num = (v: string | number | undefined) => {
  const n = Number(v ?? 0);
  return isNaN(n) ? 0 : n;
};

const LOP_REF = "LOP_SYSTEM";

type EditLine = {
  key: string;
  id?: number;
  name: string;
  amount: string;
  referenceType?: string;
  source?: string;
};

const toEditLines = (
  lines: IPayrollRunItemLine[] | undefined,
  keyPrefix: string,
): EditLine[] =>
  (lines ?? [])
    .filter((l) => (l.nameInPayslip || l.name || "").trim())
    .map((l, i) => ({
      key: l.id != null ? `id-${l.id}` : `${keyPrefix}-${i}`,
      id: l.id,
      name: l.nameInPayslip || l.name || "",
      amount: String(num(l.amount)),
      referenceType: l.referenceType,
      source: l.source as string | undefined,
    }));

/**
 * Full add/remove/modify editor (mirrors the review edit) to resolve a
 * zero/negative-net WPS exclusion. LOP shows as a deduction line (LOP_SYSTEM),
 * extracted back to lopAmount on save — same as the review edit. Editable on
 * any non-cancelled run (unlock); backend audit-logs the change.
 */
const ResolveDialog = ({
  runId,
  row,
  onClose,
  onResolved,
}: IAmountsDialogProps) => {
  const { t } = useTranslation(["company", "common"]);
  const { data, isLoading } = useGetItemDetailQuery(
    { runId, itemId: row.itemId },
    { refetchOnMountOrArgChange: true },
  );
  const [saveAmounts, { isLoading: isSaving }] = useUpdateItemAmountsMutation();
  const newKey = useRef(0);

  const detail = data?.data;
  const [earnings, setEarnings] = useState<EditLine[]>([]);
  const [benefits, setBenefits] = useState<EditLine[]>([]);
  const [deductions, setDeductions] = useState<EditLine[]>([]);
  const [bankForm, setBankForm] = useState({
    bankName: "",
    accountNumber: "",
    routingNumber: "",
    wpsPersonId: "",
  });

  // Bank fields shown + required depend on the payment method:
  //   WPS          → IBAN + Routing + WPS Person ID (bank name optional)
  //   BANK_TRANSFER→ IBAN + Bank Name
  //   CASH/CHEQUE  → no bank section at all
  const method = row.paymentMethod;
  const bankFieldKeys: string[] =
    method === "WPS"
      ? ["bankName", "accountNumber", "routingNumber", "wpsPersonId"]
      : method === "BANK_TRANSFER"
        ? ["bankName", "accountNumber"]
        : [];
  const requiredBankKeys: string[] =
    method === "WPS"
      ? ["accountNumber", "routingNumber", "wpsPersonId"]
      : method === "BANK_TRANSFER"
        ? ["accountNumber", "bankName"]
        : [];
  const bankFields = buildFields(t).filter((f) =>
    bankFieldKeys.includes(f.key),
  );
  const missingBank: Record<string, boolean> = {
    bankName:
      requiredBankKeys.includes("bankName") && !bankForm.bankName.trim(),
    accountNumber:
      requiredBankKeys.includes("accountNumber") &&
      !bankForm.accountNumber.trim(),
    routingNumber:
      requiredBankKeys.includes("routingNumber") &&
      !bankForm.routingNumber.trim(),
    wpsPersonId:
      requiredBankKeys.includes("wpsPersonId") && !bankForm.wpsPersonId.trim(),
  };
  const missingBankLabels = bankFields
    .filter((f) => missingBank[f.key])
    .map((f) => f.label);

  useEffect(() => {
    if (!detail) return;
    setEarnings(toEditLines(detail.earnings, "e"));
    setBenefits(toEditLines(detail.benefits, "b"));
    // Sanitize pre-filled values too — legacy/seed data may hold non-numeric
    // junk in numeric fields; strip it so it shows as missing (red) instead of
    // silently saving invalid data.
    setBankForm({
      bankName: detail.bankName ?? "",
      accountNumber: sanitizeBankField(
        "accountNumber",
        detail.accountNumber ?? "",
      ),
      routingNumber: sanitizeBankField(
        "routingNumber",
        detail.routingNumber ?? "",
      ),
      wpsPersonId: sanitizeBankField("wpsPersonId", detail.wpsPersonId ?? ""),
    });

    const base = toEditLines(detail.deductions, "d");
    const lopAmount = num(
      (detail as unknown as { lopAmount?: number }).lopAmount,
    );
    const hasLop = base.some((l) => l.referenceType === LOP_REF);
    setDeductions(
      lopAmount > 0 && !hasLop
        ? [
            ...base,
            {
              key: "lop-system",
              name: t("company:payrolls.loss_of_pay"),
              amount: String(lopAmount),
              referenceType: LOP_REF,
            },
          ]
        : base,
    );
  }, [detail]);

  const statutoryEmployee = (detail?.statutoryLines ?? [])
    .filter((l) => l.party === "EMPLOYEE")
    .reduce((s, l) => s + num(l.amount), 0);

  const sum = (arr: EditLine[]) => arr.reduce((s, l) => s + num(l.amount), 0);
  const estimatedNet =
    sum(earnings) + sum(benefits) - sum(deductions) - statutoryEmployee;

  const addRow =
    (setter: React.Dispatch<React.SetStateAction<EditLine[]>>) => () => {
      newKey.current += 1;
      setter((prev) => [
        ...prev,
        { key: `new-${newKey.current}`, name: "", amount: "0" },
      ]);
    };
  const updateRow =
    (setter: React.Dispatch<React.SetStateAction<EditLine[]>>) =>
    (key: string, patch: Partial<EditLine>) =>
      setter((prev) =>
        prev.map((l) => (l.key === key ? { ...l, ...patch } : l)),
      );
  const removeRow =
    (setter: React.Dispatch<React.SetStateAction<EditLine[]>>) =>
    (key: string) =>
      setter((prev) => prev.filter((l) => l.key !== key));

  const handleSave = async () => {
    if (isSaving) return;
    const toPayload = (arr: EditLine[]) =>
      arr
        .filter((l) => l.name.trim())
        .map((l) => ({
          ...(l.id != null ? { id: l.id } : {}),
          name: l.name.trim(),
          amount: num(l.amount),
          isActive: true,
          ...(l.referenceType ? { referenceType: l.referenceType } : {}),
        })) as unknown as IPayrollRunItemLine[];

    // Extract LOP back into lopAmount; send other deductions as lines.
    const lopLine = deductions.find((l) => l.referenceType === LOP_REF);
    const dedNoLop = deductions.filter((l) => l.referenceType !== LOP_REF);

    try {
      const res = await saveAmounts({
        runId,
        itemId: row.itemId,
        data: {
          earnings: toPayload(earnings),
          benefits: toPayload(benefits),
          deductions: toPayload(dedNoLop),
          lopAmount: lopLine ? num(lopLine.amount) : 0,
          bankName: bankForm.bankName.trim() || null,
          accountNumber: bankForm.accountNumber.trim() || null,
          routingNumber: bankForm.routingNumber.trim() || null,
          wpsPersonId: bankForm.wpsPersonId.trim() || null,
          reason: t("company:payrolls.fixed_to_resolve_wps_exclusion"),
        },
      }).unwrap();
      if (res.data?.wpsEligible) {
        onResolved(row.itemId);
      } else {
        const label = res.data?.reason
          ? wpsReasonLabel(t, res.data.reason)
          : t("company:payrolls.still_incomplete");
        toast.error(
          res.data && res.data.netPay <= 0
            ? t("company:payrolls.net_pay_still_not_positive")
            : t("company:payrolls.saved_but_still", {
                label: label.toLowerCase(),
              }),
        );
      }
    } catch (e: any) {
      toast.error(
        e?.data?.message ?? e?.message ?? t("company:payrolls.failed_to_save"),
      );
    }
  };

  const Section = ({
    title,
    lines,
    tone,
    setter,
  }: {
    title: string;
    lines: EditLine[];
    tone: "earning" | "deduction";
    setter: React.Dispatch<React.SetStateAction<EditLine[]>>;
  }) => (
    <div className="flex flex-col gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </span>
      {lines.length === 0 && (
        <span className="py-1 text-xs text-muted-foreground">
          {t("company:payrolls.no_items")}
        </span>
      )}
      {lines.map((l) => (
        <div key={l.key} className="flex items-center gap-2">
          <span
            className={cn(
              "w-4 text-center text-sm font-bold",
              tone === "earning" ? "text-success" : "text-danger",
            )}
          >
            {tone === "earning" ? "+" : "−"}
          </span>
          <input
            type="text"
            value={l.name}
            onChange={(e) => updateRow(setter)(l.key, { name: e.target.value })}
            placeholder={t("common:labels.name")}
            readOnly={l.referenceType === LOP_REF}
            className="h-9 flex-1 rounded-xs border border-input px-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
          />
          {(l.source === "PENALTY" || l.referenceType === "Penalty") && (
            <span className="rounded-full bg-warning-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase text-warning">
              {t("company:payrolls.penalty")}
            </span>
          )}
          <input
            type="number"
            value={l.amount}
            onChange={(e) =>
              updateRow(setter)(l.key, { amount: e.target.value })
            }
            className="h-9 w-28 rounded-xs border border-input px-2 text-end text-sm tabular-nums focus:outline-hidden focus:ring-2 focus:ring-ring"
          />
          <button
            type="button"
            onClick={() => removeRow(setter)(l.key)}
            className="text-muted-foreground hover:text-danger"
            aria-label={t("common:buttons.remove")}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow(setter)}
        className="inline-flex items-center gap-1.5 self-start rounded-xs border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-semibold text-primary hover:bg-primary/10"
      >
        <Plus className="h-3.5 w-3.5" />
        {t("company:payrolls.add_line_of_type", {
          type: title.toLowerCase(),
        })}
      </button>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("company:payrolls.fix_and_resolve")}</DialogTitle>
          <DialogDescription>
            {t("company:payrolls.wps_excluded_dialog_desc", {
              name: row.name ?? t("company:payrolls.this_employee"),
            })}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="flex max-h-[50vh] flex-col gap-4 overflow-y-auto scrollbar-thin pe-1">
              {/* Bank details — only for methods that need them (Cash/Cheque skip). */}
              {bankFields.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("my-space:ui.bank_details")}
                  </span>
                  {missingBankLabels.length > 0 && (
                    <div className="rounded-xs border border-danger-muted bg-danger-muted/40 px-3 py-2 text-xs text-danger">
                      <Trans
                        i18nKey="company:payrolls.missing_required_fields"
                        values={{ fields: missingBankLabels.join(", ") }}
                        components={{ 1: <b /> }}
                      />
                    </div>
                  )}
                  {bankFields.map((f) => {
                    const isMissing = missingBank[f.key];
                    return (
                      <div key={f.key} className="flex items-center gap-2">
                        <span
                          className={cn(
                            "w-28 shrink-0 text-xs",
                            isMissing
                              ? "font-semibold text-danger"
                              : "text-muted-foreground",
                          )}
                        >
                          {f.label}
                          {isMissing && " *"}
                        </span>
                        <input
                          type="text"
                          inputMode={
                            f.key === "routingNumber" || f.key === "wpsPersonId"
                              ? "numeric"
                              : "text"
                          }
                          value={bankForm[f.key]}
                          onChange={(e) =>
                            setBankForm((prev) => ({
                              ...prev,
                              [f.key]: sanitizeBankField(f.key, e.target.value),
                            }))
                          }
                          placeholder={f.placeholder}
                          className={cn(
                            "h-9 flex-1 rounded-xs border px-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring",
                            isMissing
                              ? "border-danger bg-danger-muted/20"
                              : "border-input",
                          )}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              <Section
                title={t("company:payrolls.earnings")}
                lines={earnings}
                tone="earning"
                setter={setEarnings}
              />
              <Section
                title={t("company:payrolls.benefits")}
                lines={benefits}
                tone="earning"
                setter={setBenefits}
              />
              <Section
                title={t("company:payrolls.deductions")}
                lines={deductions}
                tone="deduction"
                setter={setDeductions}
              />

              {statutoryEmployee > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{t("company:payrolls.statutory_employee")}</span>
                  <span className="tabular-nums">
                    −{statutoryEmployee.toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <div
              className={cn(
                "mt-1 flex items-center justify-between rounded-xs border px-3 py-2.5",
                estimatedNet > 0
                  ? "border-success-muted bg-success-muted/40"
                  : "border-danger-muted bg-danger-muted/40",
              )}
            >
              <span className="text-sm font-semibold text-foreground">
                {t("company:payrolls.estimated_net_pay")}
              </span>
              <span
                className={cn(
                  "text-sm font-bold tabular-nums",
                  estimatedNet > 0 ? "text-success" : "text-danger",
                )}
              >
                {estimatedNet.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            isLoading={isSaving}
            disabled={isLoading || estimatedNet <= 0}
          >
            {t("company:payrolls.save_and_resolve")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
